import { notifications } from '@mantine/notifications';
import { IconCheck, IconExclamationCircle, IconMailCheck, IconMailX } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { EmailLevel, User } from '@xver/@types/user';
import { Endpoints } from '@xver/apis';
import { Button } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { getErrorMessage } from '@xver/utils/error';
import storage from '@xver/utils/storage';

interface EmailVerificationSectionProps {
    user: User;
}

export default function EmailVerificationSection({ user }: EmailVerificationSectionProps) {
    const { t } = useTranslation('security');
    const verified = user.verifyEmailAddress === EmailLevel.E2;

    const mutation = useMutation({
        mutationFn: () => {
            const token = storage.get<string>('_zugang', '');
            if (!token) throw new Error(t('errors.missingToken'));
            return Endpoints.v1.Authentication.sendEmailVerification(token);
        },
        onSuccess: () => {
            notifications.show({
                title: t('toast.emailVerifyTitle'),
                message: t('toast.emailVerifyMessage'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
        },
        onError: (err: unknown) => {
            notifications.show({
                title: t('toast.errorTitle'),
                message: getErrorMessage(err) ?? t('toast.errorMessage'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    return (
        <DialogBox
            label={t('emailVerification.title')}
            description={t('emailVerification.description')}
            classNames="p-4"
        >
            <div className="w-full flex flex-row items-center justify-between gap-4">
                <div className="inline-flex items-center gap-2">
                    {verified ? (
                        <IconMailCheck size={20} color="#22c55e" stroke={1.5} />
                    ) : (
                        <IconMailX size={20} color="#eb5757" stroke={1.5} />
                    )}
                    <span className="text-sm">
                        {verified
                            ? t('emailVerification.verified')
                            : t('emailVerification.unverified')}
                    </span>
                </div>
                {!verified && (
                    <Button
                        size="sm"
                        onClick={() => mutation.mutate()}
                        isLoading={mutation.isPending}
                    >
                        {t('emailVerification.send')}
                    </Button>
                )}
            </div>
        </DialogBox>
    );
}
